/**
 * Reprova a identificação de UMA fatura dentro de um pagamento.
 *
 * ⚠️ Antes isso não existia: remover a sinalização desfazia o pagamento INTEIRO, porque a conta
 * dele tinha de fechar e tirar uma fatura do meio deixava as irmãs numa conta quebrada. A marcação
 * parcial (AF-3671 step 4) tornou a conta aberta legítima, e com ela a remoção por item passou a
 * ser uma operação que alguém consegue retomar: o pagamento volta a ter saldo a identificar e a
 * tela diz quanto.
 *
 * As irmãs marcadas ficam onde estão. É a diferença para a "Reprovar Identificação" da tela de
 * Pagamentos, que devolve o lote todo.
 *
 * O motivo é obrigatório, como em toda remoção de sinalização: quem vai perguntar "por que minha
 * marcação sumiu?" é a cobrança, dias depois, olhando o histórico da FATURA.
 */
import { useState } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { PaymentRow, unmarkInvoices } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';

interface Props {
    payment: PaymentRow;
    invoice: any;
    onClose: () => void;
    onUnmarked: () => void;
}

const UnmarkOneInvoiceDialog: React.FC<Props> = ({ payment, invoice, onClose, onUnmarked }) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const confirm = async () => {
        setSubmitting(true);
        try {
            await unmarkInvoices(payment.paymentId, reason.trim(), [invoice.invoiceId]);
            toast.success('Sinalização removida desta fatura.');
            onUnmarked();
            onClose();
        } catch (e: any) {
            toast.error(`Não foi possível reprovar: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <FaExclamationTriangle className="text-amber-500" /> Reprovar esta fatura
                    </h3>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="px-6 py-4 space-y-3">
                    <p className="text-sm text-gray-700 dark:text-gray-200">
                        {invoice.contractLabel && <>{invoice.contractLabel} · </>}
                        {invoice.details?.documentTitle || `fatura ${String(invoice.invoiceId).slice(0, 8)}`}
                        {' · '}<strong>{formatBRL(invoice.amountPaid || invoice.amount || 0)}</strong>
                    </p>

                    <p className="text-sm px-3 py-2 rounded bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        Só esta fatura perde a sinalização — as outras deste pagamento continuam
                        marcadas. O valor volta a ficar disponível para identificar.
                    </p>

                    <div>
                        <label className="block text-sm font-medium mb-1" htmlFor="motivo-reprovacao-fatura">
                            Motivo da reprovação *
                        </label>
                        <textarea
                            id="motivo-reprovacao-fatura"
                            className="w-full text-sm border rounded p-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                            rows={3}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="Por que esta fatura não é paga por este pagamento"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Vai para o histórico da fatura — é o que a cobrança vai ler quando
                            procurar a marcação que sumiu.
                        </p>
                    </div>
                </div>

                <div className="px-6 py-3 border-t dark:border-slate-600 flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    <button onClick={confirm} disabled={submitting || !reason.trim()}
                        className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                        {submitting ? 'Reprovando...' : 'Reprovar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnmarkOneInvoiceDialog;
